import React, { useEffect, useState, useRef, memo } from "react";
import { useReactFlow } from "reactflow";

const LineNode = memo(function LineNode({ data, selected, width: nodeWidth, height: nodeHeight, id, broadcast, nodesRef, edgesRef }) {
  // 선의 실제 길이 (대각선 길이)
  const lineWidth = data?.lineLength || data?.width || 200;
  
  // 바운딩 박스 크기
  const width = nodeWidth || data?.boundingWidth || 10;
  const height = nodeHeight || data?.boundingHeight || 10;
  const { 
    stroke = "#000000", 
    strokeWidth = 5,
    strokeOpacity = 1,
    strokeRgba,
    strokeDasharray = 'none',
    arrowEnd = false,
    arrowStart = false,
    rotation = 0,
    centerOffsetX = width / 2, // 바운딩 박스 내 선 중심의 X 오프셋
    centerOffsetY = height / 2  // 바운딩 박스 내 선 중심의 Y 오프셋
  } = data || {};

  const { setNodes, getNode, screenToFlowPosition } = useReactFlow();
  const [isRotating, setIsRotating] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null);
  const rotationStartRef = useRef({ angle: 0 });
  const resizeStartRef = useRef(null);

  const [isShiftPressed, setIsShiftPressed] = useState(false);
  const nodeRef = useRef(null);
  useEffect(() => {
    if (!selected) return;
    const onKey = (e) => { if (e.key === 'Shift') setIsShiftPressed(e.type === 'keydown'); };
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keyup', onKey);
      setIsShiftPressed(false);
    };
  }, [selected]);

  // 선 색상 (투명도 포함)
  const strokeColor = strokeRgba || (strokeOpacity < 1
    ? (() => {
        const r = parseInt(stroke.slice(1, 3), 16);
        const g = parseInt(stroke.slice(3, 5), 16);
        const b = parseInt(stroke.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${strokeOpacity})`;
      })()
    : stroke);
  
  // 화살표용 색상 (투명도 포함) - strokeColor 사용
  // SVG marker는 rgba를 직접 사용해야 투명도가 제대로 반영됨
  const arrowColor = strokeColor;

  // 바운딩 박스 내에서 선의 중심을 기준으로 양 끝점 계산
  const halfLineWidth = lineWidth / 2;
  const x1 = centerOffsetX - halfLineWidth;
  const y1 = centerOffsetY;
  const x2 = centerOffsetX + halfLineWidth;
  const y2 = centerOffsetY;

  // Marker ID에 투명도를 포함시켜 캐싱 문제 방지
  const markerEndId = `arrow-end-${id}-${strokeOpacity.toFixed(2)}`;
  const markerStartId = `arrow-start-${id}-${strokeOpacity.toFixed(2)}`;

  const getDashArray = () => {
    if (strokeDasharray === 'none') return 'none';
    // 스펙트럼 값 (예: '2,2', '4,4' 등) 또는 기존 값 ('dashed', 'dotted')
    if (strokeDasharray === 'dashed') return '10,5';
    if (strokeDasharray === 'dotted') return '2,3';
    // 직접 지정된 값 (예: '15,15', '20,20' 등)
    return strokeDasharray;
  };

  // 회전 핸들 시작
  const handleRotationStart = (e) => {
    e.stopPropagation();
    e.preventDefault();
    
    const nodeElement = e.currentTarget.parentElement;
    if (!nodeElement) return; // null 체크
    const rect = nodeElement.getBoundingClientRect();
    // 선의 실제 중심점 계산 (바운딩 박스 내 centerOffset 위치)
    const centerX = rect.left + centerOffsetX;
    const centerY = rect.top + centerOffsetY;
    
    const angle = Math.atan2(e.clientY - centerY, e.clientX - centerX) * (180 / Math.PI);
    
    rotationStartRef.current = {
      angle: rotation - angle,
    };
    
    setIsRotating(true);
  };

  // 리사이즈 핸들 시작
  const handleResizeStart = (e, handle) => {
    e.stopPropagation();
    e.preventDefault();
    
    const node = getNode(id);
    if (!node) return;
    
    // 회전 각도를 라디안으로 변환
    const rotationRad = (rotation * Math.PI) / 180;
    
    // ReactFlow 좌표계에서 현재 선의 중심점 (바운딩 박스 position + centerOffset)
    const centerX = node.position.x + centerOffsetX;
    const centerY = node.position.y + centerOffsetY;
    
    // 현재 선의 양 끝점 계산 (ReactFlow 좌표)
    const halfLineWidth = lineWidth / 2;
    const leftEndX = centerX - halfLineWidth * Math.cos(rotationRad);
    const leftEndY = centerY - halfLineWidth * Math.sin(rotationRad);
    const rightEndX = centerX + halfLineWidth * Math.cos(rotationRad);
    const rightEndY = centerY + halfLineWidth * Math.sin(rotationRad);
    
    resizeStartRef.current = {
      mouseFlow: screenToFlowPosition({ x: e.clientX, y: e.clientY }),
      lineWidth: lineWidth,
      position: { ...node.position },
      rotation: rotation,
      leftEndX,
      leftEndY,
      rightEndX,
      rightEndY,
    };
    
    setResizeHandle(handle);
    setIsResizing(true);
  };

  // 회전 중
  useEffect(() => {
    if (!isRotating) {
      return;
    }

    const handleRotationMove = (e) => {
      const nodeElement = document.getElementById(`node-${id}`);
      if (!nodeElement) return;

      const rect = nodeElement.getBoundingClientRect();
      // 선의 실제 중심점 계산 (바운딩 박스 내 centerOffset 위치)
      const centerX = rect.left + centerOffsetX;
      const centerY = rect.top + centerOffsetY;
      
      const angle = Math.atan2(e.clientY - centerY, e.clientX - centerX) * (180 / Math.PI);
      const newRotation = rotationStartRef.current.angle + angle;
      
      setNodes((nodes) => {
        const updatedNodes = nodes.map((node) =>
          node.id === id
            ? { ...node, data: { ...node.data, rotation: newRotation } }
            : node
        );
        
        // nodesRef도 즉시 업데이트
        if (nodesRef) {
          nodesRef.current = updatedNodes;
        }
        
        return updatedNodes;
      });
    };

    const handleRotationEnd = () => {
      setIsRotating(false);
      
      // 회전 완료 후 서버에 부분 업데이트 전송
      if (broadcast && nodesRef && edgesRef) {
        const currentNodes = nodesRef.current;
        const currentEdges = edgesRef.current;
        broadcast(currentNodes, currentEdges, true, [id]);
      }
    };

    document.addEventListener('mousemove', handleRotationMove);
    document.addEventListener('mouseup', handleRotationEnd);

    return () => {
      document.removeEventListener('mousemove', handleRotationMove);
      document.removeEventListener('mouseup', handleRotationEnd);
    };
  }, [isRotating, id, rotation, setNodes, broadcast, nodesRef, edgesRef, centerOffsetX, centerOffsetY]);

  // 리사이즈 중
  useEffect(() => {
    if (!isResizing || !resizeStartRef.current) return;

    const handleResizeMove = (e) => {
      const { leftEndX, leftEndY, rightEndX, rightEndY } = resizeStartRef.current;
      
      // 현재 마우스 위치를 ReactFlow 좌표로 변환
      const mouseFlow = screenToFlowPosition({ x: e.clientX, y: e.clientY });
      
      // 실제 왼쪽/오른쪽 끝점 계산
      let actualLeftEndX, actualLeftEndY, actualRightEndX, actualRightEndY;
      
      // Shift 키를 누르면 각도를 유지하면서 길이만 조정
      if (isShiftPressed) {
        // 고정된 끝점 설정
        const fixedEndX = resizeHandle === 'left' ? rightEndX : leftEndX;
        const fixedEndY = resizeHandle === 'left' ? rightEndY : leftEndY;
        
        // 마우스 위치와 고정 끝점 사이의 거리 계산
        const dx = mouseFlow.x - fixedEndX;
        const dy = mouseFlow.y - fixedEndY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        // 원래 각도 유지 (라디안)
        const originalRotation = (rotation * Math.PI) / 180;
        
        // 각도를 유지하면서 새로운 끝점 계산
        if (resizeHandle === 'left') {
          actualRightEndX = rightEndX;
          actualRightEndY = rightEndY;
          actualLeftEndX = rightEndX - distance * Math.cos(originalRotation);
          actualLeftEndY = rightEndY - distance * Math.sin(originalRotation);
        } else {
          actualLeftEndX = leftEndX;
          actualLeftEndY = leftEndY;
          actualRightEndX = leftEndX + distance * Math.cos(originalRotation);
          actualRightEndY = leftEndY + distance * Math.sin(originalRotation);
        }
      } else {
        // 일반 리사이즈 (각도도 변경 가능)
        // 드래그하는 쪽이 마우스를 따라감, 반대쪽 고정
        if (resizeHandle === 'left') {
          // 왼쪽 리사이저 드래그: 왼쪽이 마우스를 따라가고 오른쪽 고정
          actualRightEndX = rightEndX;
          actualRightEndY = rightEndY;
          actualLeftEndX = mouseFlow.x;
          actualLeftEndY = mouseFlow.y;
        } else if (resizeHandle === 'right') {
          // 오른쪽 리사이저 드래그: 오른쪽이 마우스를 따라가고 왼쪽 고정
          actualLeftEndX = leftEndX;
          actualLeftEndY = leftEndY;
          actualRightEndX = mouseFlow.x;
          actualRightEndY = mouseFlow.y;
        }
      }
      
      // 두 끝점 사이의 거리 = 새로운 width (왼쪽에서 오른쪽으로)
      const dx = actualRightEndX - actualLeftEndX;
      const dy = actualRightEndY - actualLeftEndY;
      const newWidth = Math.sqrt(dx * dx + dy * dy);
      
      // 최소 크기 제한
      if (newWidth < 50) return;
      
      // 새로운 회전 각도 계산 (항상 왼쪽에서 오른쪽으로의 각도)
      const newRotation = Math.atan2(dy, dx) * (180 / Math.PI);
      
      // 두 리사이저 점의 AABB(Axis-Aligned Bounding Box) 계산
      const minX = Math.min(actualLeftEndX, actualRightEndX);
      const maxX = Math.max(actualLeftEndX, actualRightEndX);
      const minY = Math.min(actualLeftEndY, actualRightEndY);
      const maxY = Math.max(actualLeftEndY, actualRightEndY);
      
      // 바운딩 박스의 width, height (SelectBox에 사용됨)
      const boundingWidth = maxX - minX;
      const boundingHeight = maxY - minY;
      
      // 최소 크기 보장 (너무 작으면 선택이 어려움)
      const finalBoundingWidth = Math.max(boundingWidth, 10);
      const finalBoundingHeight = Math.max(boundingHeight, 10);
      
      // 새로운 중심점 계산 (실제 선의 중심)
      const newCenterX = (actualLeftEndX + actualRightEndX) / 2;
      const newCenterY = (actualLeftEndY + actualRightEndY) / 2;
      
      // 바운딩 박스의 왼쪽 위 모서리를 position으로 설정
      const newPositionX = minX;
      const newPositionY = minY;
      
      // 바운딩 박스 내에서의 선의 중심점 (상대 좌표)
      const relativeCenterX = newCenterX - newPositionX;
      const relativeCenterY = newCenterY - newPositionY;
      
      setNodes((nodes) => {
        const updatedNodes = nodes.map((node) => {
          if (node.id !== id) return node;
          
          return {
            ...node,
            position: {
              x: newPositionX,
              y: newPositionY
            },
            width: finalBoundingWidth, // ⭐ 바운딩 박스 width
            height: finalBoundingHeight, // ⭐ 바운딩 박스 height
            data: {
              ...node.data,
              lineLength: newWidth, // ⭐ 선의 실제 길이
              // ⭐ 바운딩 박스 크기도 data에 저장
              boundingWidth: finalBoundingWidth,
              boundingHeight: finalBoundingHeight,
              rotation: newRotation,
              // 바운딩 박스 내에서의 선 중심점 오프셋 저장
              centerOffsetX: relativeCenterX,
              centerOffsetY: relativeCenterY
            }
          };
        });
        
        // ⭐ nodesRef도 즉시 업데이트
        if (nodesRef) {
          nodesRef.current = updatedNodes;
        }
        
        return updatedNodes;
      });
    };

    const handleResizeEnd = () => {
      setIsResizing(false);
      setResizeHandle(null);
      
      // 리사이즈 완료 후 서버에 부분 업데이트 전송
      if (broadcast && nodesRef && edgesRef) {
        const currentNodes = nodesRef.current;
        const currentEdges = edgesRef.current;
        broadcast(currentNodes, currentEdges, true, [id]);
      }
    };

    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);

    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
    };
  }, [isResizing, resizeHandle, id, lineWidth, centerOffsetX, centerOffsetY, setNodes, screenToFlowPosition, broadcast, nodesRef, edgesRef, isShiftPressed, rotation]);

  return (
    <>
      <div 
        ref={nodeRef}
        id={`node-${id}`}
        style={{ 
          width, 
          height, 
          position: 'relative',
          transform: `rotate(${rotation}deg)`,
          transformOrigin: `${centerOffsetX}px ${centerOffsetY}px`,
          overflow: 'visible'
        }}
      >
      
      {/* 리사이즈 핸들 */}
      {selected && (
        <>
          <div
            className="nodrag nopan"
            onMouseDown={(e) => handleResizeStart(e, 'left')}
            style={{
              position: 'absolute',
              left: `${x1 - 10}px`,
              top: `${y1 - 10}px`,
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              backgroundColor: '#ffffff',
              border: '4px solid #000000',
              boxSizing: 'border-box',
              cursor: 'ew-resize',
              zIndex: 1000,
              pointerEvents: 'auto',
            }}
            title="크기 조절"
          />
          
          <div
            className="nodrag nopan"
            onMouseDown={(e) => handleResizeStart(e, 'right')}
            style={{
              position: 'absolute',
              left: `${x2 - 10}px`,
              top: `${y2 - 10}px`,
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              backgroundColor: '#ffffff',
              border: '4px solid #000000',
              boxSizing: 'border-box',
              cursor: 'ew-resize',
              zIndex: 1000,
              pointerEvents: 'auto',
            }}
            title="크기 조절"
          />
        </>
      )}
      

      
      {/* SVG 선 */}
      <svg 
        width={width} 
        height={height} 
        style={{ 
          display: 'block',
          overflow: 'visible',
        }}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          {arrowEnd && (
            <marker
              id={markerEndId}
              markerWidth="7"
              markerHeight="7"
              refX="0"
              refY="2"
              orient="auto"
              markerUnits="strokeWidth"
            >
              <path
                d="M0,0 L0,4 L4.5,2 z"
                fill={arrowColor}
                style={{ fill: arrowColor }}
              />
            </marker>
          )}
          {arrowStart && (
            <marker
              id={markerStartId}
              markerWidth="7"
              markerHeight="7"
              refX="4.5"
              refY="2"
              orient="auto"
              markerUnits="strokeWidth"
            >
              <path
                d="M4.5,0 L4.5,4 L0,2 z"
                fill={arrowColor}
                style={{ fill: arrowColor }}
              />
            </marker>
          )}
        </defs>
        {/* 투명한 넓은 선 - 클릭 영역 확대 */}
        <line
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          stroke="transparent"
          strokeWidth={Math.max(20, strokeWidth)}
          strokeLinecap="butt"
          style={{
            cursor: 'pointer',
          }}
        />
        {/* 실제 보이는 선 */}
        <line
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          strokeLinecap="butt"
          strokeDasharray={getDashArray()}
          markerEnd={arrowEnd ? `url(#${markerEndId})` : undefined}
          markerStart={arrowStart ? `url(#${markerStartId})` : undefined}
          vectorEffect="non-scaling-stroke"
          shapeRendering="geometricPrecision"
          style={{
            pointerEvents: 'none',
          }}
        />
      </svg>
    </div>

  </>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.id === nextProps.id &&
    prevProps.selected === nextProps.selected &&
    prevProps.width === nextProps.width &&
    prevProps.height === nextProps.height &&
    prevProps.data?.lineLength === nextProps.data?.lineLength &&
    prevProps.data?.boundingWidth === nextProps.data?.boundingWidth &&
    prevProps.data?.boundingHeight === nextProps.data?.boundingHeight &&
    prevProps.data?.stroke === nextProps.data?.stroke &&
    prevProps.data?.strokeWidth === nextProps.data?.strokeWidth &&
    prevProps.data?.strokeOpacity === nextProps.data?.strokeOpacity &&
    prevProps.data?.strokeRgba === nextProps.data?.strokeRgba &&
    prevProps.data?.strokeDasharray === nextProps.data?.strokeDasharray &&
    prevProps.data?.arrowEnd === nextProps.data?.arrowEnd &&
    prevProps.data?.arrowStart === nextProps.data?.arrowStart &&
    prevProps.data?.rotation === nextProps.data?.rotation &&
    prevProps.data?.centerOffsetX === nextProps.data?.centerOffsetX &&
    prevProps.data?.centerOffsetY === nextProps.data?.centerOffsetY &&
    prevProps.data?.selectedBy === nextProps.data?.selectedBy &&
    prevProps.data?.showUserLabel === nextProps.data?.showUserLabel &&
    prevProps.broadcast === nextProps.broadcast &&
    prevProps.nodesRef === nextProps.nodesRef &&
    prevProps.edgesRef === nextProps.edgesRef
  );
});

export default LineNode;
